import { ReactNode } from 'react';

import { Diagnosis, EquipUIFireflyV2Theme, Note, User } from '@equip.health/ui';

const { primary } = EquipUIFireflyV2Theme.colors;
const userIcon = <User fill={primary[100]} height="16px" width="16px" />;
const noteIcon = <Note fill={primary[100]} height="16px" width="16px" />;
const diagnosisIcon = (
  <Diagnosis fill={primary[100]} height="16px" width="16px" />
);

export enum ActivityTypeEnum {
  ADMIN_EPISODE = 'ADMIN_EPISODE',
  AUTH_DETAIL = 'AUTH_DETAIL',
  DIAGNOSIS = 'DIAGNOSIS',
  INSURANCE_VERIFICATION = 'INSURANCE_VERIFICATION',
  MEDICAL_CLEARANCE = 'MEDICAL_CLEARANCE',
  PATIENT_ACCOUNT = 'PATIENT_ACCOUNT',
  PATIENT_ADDRESS = 'PATIENT_ADDRESS',
  PATIENT_BASIC_INFO = 'PATIENT_BASIC_INFO',
  PATIENT_CONTACT_PREFERENCES = 'PATIENT_CONTACT_PREFERENCES',
  PATIENT_CONSENT_INFO = 'PATIENT_CONSENT_INFO',
  PATIENT_ETHNICITY = 'PATIENT_ETHNICITY',
  PATIENT_LANGUAGE = 'PATIENT_LANGUAGE',
  PATIENT_PRONOUNS_OFF_RECORD = 'PATIENT_PRONOUNS_OFF_RECORD',
  PATIENT_PRONOUNS_ON_RECORD = 'PATIENT_PRONOUNS_ON_RECORD',
  PATIENT_SUPPORT_ADDRESS = 'PATIENT_SUPPORT_ADDRESS',
  PATIENT_TAGS = 'PATIENT_TAGS',
  PAUSED_DETAIL = 'PAUSED_DETAIL',
  PSYCH_CLEARANCE = 'PSYCH_CLEARANCE',
  SUPPORT_ACCOUNT = 'SUPPORT_ACCOUNT',
  SUPPORT_BASIC_INFO = 'SUPPORT_BASIC_INFO',
  SUPPORT_CONTACT_PREFERENCES = 'SUPPORT_CONTACT_PREFERENCES',
  SUPPORT_ETHNICITY = 'SUPPORT_ETHNICITY',
  SUPPORT_LANGUAGE = 'SUPPORT_LANGUAGE',
  SUPPORT_PRONOUNS = 'SUPPORT_PRONOUNS',
  SUPPORT_PATIENT_RELATION = 'SUPPORT_PATIENT_RELATION',
  TREATMENT_EPISODE = 'TREATMENT_EPISODE',
}
export const adminEpisodeFieldMapper = {
  closed_date: 'Episode closed date',
  episode_length_weeks: 'Episode length weeks',
  episode_start_date: 'Episode start date',
  episode_status_code: 'Episode status',
  first_inquiry_referral_date: 'Episode first inquiry referral date',
  is_archived: 'Episode is archived',
  level_label: 'Episode level',
  program_name: 'Episode program name',
  status_reason_name: 'Episode status reason',
};
export const treatmentEpisodeFieldMapper = {
  admit_date: 'Episode admit date',
  closed_date: 'Episode closed date',
  episode_length_weeks: 'Episode length weeks',
  level_label: 'Episode level',
  program_name: 'Episode program name',
  status_code: 'Episode status code',
  status_reason_name: 'Episode status reason',
};
export const medicalClearanceFieldMapper = {
  completed_date: 'Medical clearance completed date',
  label: 'Medical clearance status',
  requested_date: 'Medical clearance requested date',
};
export const psychClearanceFieldMapper = {
  completed_date: 'Psych clearance completed date',
  label: 'Psych clearance status',
  requested_date: 'Psych clearance requested date',
};
export const pausedDetailsFieldMapper = {
  end_date: 'Paused end date',
  is_deleted: 'Paused is deleted',
  start_date: 'Paused start date',
  status_reason_name: 'Paused reason',
};
export const insuranceVerificationFieldMapper = {
  completed_date: 'Insurance verification completed date',
  label: 'Insurance verification status',
  requested_date: 'Insurance verification requested date',
};
export const authDetailsFieldMapper = {
  approved_session_units: 'Authorization approved session units',
  approved_sessions: 'Authorization approved sessions',
  authorization_end_date: 'Authorization end date',
  authorization_number: 'Authorization number',
  authorization_start_date: 'Authorization start date',
  is_auth_required: 'Is auth required',
  payor_name: 'Authorization payor name',
  payor_program_name: 'Authorization payor program name',
};
export const patientBasicInfoFieldMapper = {
  cell_phone: 'Patient phone number',
  chosen_name: 'Patient chosen name',
  date_of_birth: 'Patient date of birth',
  email: 'Patient email',
  fax_number: 'Patient fax number',
  first_name: 'Patient first name',
  gender_off_record: 'Patient gender off record',
  gender_on_record: 'Patient gender on record',
  home_phone: 'Patient home phone',
  last_name: 'Patient last name',
  mrn: 'Patient mrn',
  office_extension: 'Patient office extension',
  office_number: 'Patient office number',
  secondary_cell_phone: 'Patient secondary cell phone',
  sex_name: 'Patient sex',
  timezone_name: 'Patient time zone',
};
export const patientBasicInfoAddressFieldMapper = {
  address_line1: 'Patient address line1',
  address_line2: 'Patient address line2',
  affiliation_name: 'Patient affiliation name',
  city: 'Patient city',
  country: 'Patient country',
  county: 'Patient county',
  latitude: 'Patient latitude',
  longitude: 'Patient longitude',
  state: 'Patient state',
  zip_code: 'Patient zip code',
};
export const patientPronounOffRecordFieldMapper = {
  pronoun_name: 'Patient pronoun off record',
};
export const patientPronounOnRecordFieldMapper = {
  pronoun_name: 'Patient pronoun on record',
};
export const patientEthnicityFieldMapper = {
  ethnicity_name: 'Patient ethnicity',
};
export const patientLanguageFieldMapper = {
  language_name: 'Patient language(s) spoken',
};
export const patientContactPreferencesFieldMapper = {
  contact_preference_name: 'Patient contact preferences',
};
export const patientConsentToRecordFieldMapper = {
  has_given_recording_consent: 'Consent to record',
};
export const patientAccountFieldMapper = {
  status_name: 'Patient account status',
};
export const supportAccountFieldMapper = {
  status_name: 'Support account status',
};
export const supportAddressFieldMapper = {
  address_line1: 'Support address line1',
  address_line2: 'Support address line2',
  affiliation_name: 'Support affiliation name',
  city: 'Support city',
  country: 'Support country',
  county: 'Support county',
  latitude: 'Support latitude',
  longitude: 'Support longitude',
  state: 'Support state',
  zip_code: 'Support zip code',
};
export const supportContactPreferencesFieldMapper = {
  contact_preference_name: 'Support contact preferences',
};
export const supportPronounsFieldMapper = {
  pronoun_name: 'Support pronoun',
};
export const supportLanguagesFieldMapper = {
  language_name: 'Support language(s) spoken',
};
export const supportBasicInfoFieldMapper = {
  cell_phone: 'Support phone number',
  chosen_name: 'Support chosen name',
  date_of_birth: 'Support date of birth',
  email: 'Support email',
  first_name: 'Support first name',
  gender_name: 'Support gender name',
  last_name: 'Support last name',
  middle_name: 'Support middle name',
  prefix: 'Support prefix',
  suffix: 'Support suffix',
  timezone_name: 'Support time zone',
};

export const supportEthnicityFieldMapper = {
  ethnicity_name: 'Support ethnicity',
};
export const diagnosisFieldMapper = {
  diagnosis_status_label: 'Diagnosis status',
  diagnosis_status_reason_label: 'Diagnosis status reason',
  end_date: 'Diagnosis end date',
  is_primary: 'Is primary diagnosis',
  onset_date: 'Diagnosis onset date',
};
export const supportRelationToPatientFieldMapper = {
  relation: 'Relationship to patient',
};
export const tagsFieldMapper = {
  label: 'Tag',
};

export const activityTypeMap: Record<
  ActivityTypeEnum,
  {
    icon: ReactNode;
    label: string;
    mapper: any;
  }
> = {
  [ActivityTypeEnum.ADMIN_EPISODE]: {
    icon: noteIcon,
    label: 'Admin episode',
    mapper: adminEpisodeFieldMapper,
  },
  [ActivityTypeEnum.TREATMENT_EPISODE]: {
    icon: noteIcon,
    label: 'Treatment episode',
    mapper: treatmentEpisodeFieldMapper,
  },
  [ActivityTypeEnum.DIAGNOSIS]: {
    icon: diagnosisIcon,
    label: 'Diagnosis',
    mapper: diagnosisFieldMapper,
  },
  [ActivityTypeEnum.MEDICAL_CLEARANCE]: {
    icon: noteIcon,
    label: 'Medical clearance',
    mapper: medicalClearanceFieldMapper,
  },
  [ActivityTypeEnum.PSYCH_CLEARANCE]: {
    icon: noteIcon,
    label: 'Psych clearance',
    mapper: psychClearanceFieldMapper,
  },
  [ActivityTypeEnum.PAUSED_DETAIL]: {
    icon: noteIcon,
    label: 'Paused details',
    mapper: pausedDetailsFieldMapper,
  },
  [ActivityTypeEnum.INSURANCE_VERIFICATION]: {
    icon: noteIcon,
    label: 'Insurance verification',
    mapper: insuranceVerificationFieldMapper,
  },
  [ActivityTypeEnum.AUTH_DETAIL]: {
    icon: noteIcon,
    label: 'Auth details',
    mapper: authDetailsFieldMapper,
  },
  [ActivityTypeEnum.PATIENT_BASIC_INFO]: {
    icon: userIcon,
    label: 'Patient info',
    mapper: patientBasicInfoFieldMapper,
  },
  [ActivityTypeEnum.PATIENT_ADDRESS]: {
    icon: userIcon,
    label: 'Patient address',
    mapper: patientBasicInfoAddressFieldMapper,
  },
  [ActivityTypeEnum.PATIENT_PRONOUNS_OFF_RECORD]: {
    icon: userIcon,
    label: 'Patient pronouns off record',
    mapper: patientPronounOffRecordFieldMapper,
  },
  [ActivityTypeEnum.PATIENT_PRONOUNS_ON_RECORD]: {
    icon: userIcon,
    label: 'Patient pronouns on record',
    mapper: patientPronounOnRecordFieldMapper,
  },
  [ActivityTypeEnum.PATIENT_ETHNICITY]: {
    icon: userIcon,
    label: 'Patient ethnicity',
    mapper: patientEthnicityFieldMapper,
  },
  [ActivityTypeEnum.PATIENT_LANGUAGE]: {
    icon: userIcon,
    label: 'Patient language(s) spoken',
    mapper: patientLanguageFieldMapper,
  },
  [ActivityTypeEnum.PATIENT_CONTACT_PREFERENCES]: {
    icon: userIcon,
    label: 'Patient contact preferences',
    mapper: patientContactPreferencesFieldMapper,
  },
  [ActivityTypeEnum.PATIENT_CONSENT_INFO]: {
    icon: userIcon,
    label: 'Patient consent to record',
    mapper: patientConsentToRecordFieldMapper,
  },
  [ActivityTypeEnum.PATIENT_ACCOUNT]: {
    icon: userIcon,
    label: 'Patient account',
    mapper: patientAccountFieldMapper,
  },
  [ActivityTypeEnum.SUPPORT_ACCOUNT]: {
    icon: userIcon,
    label: 'Support account',
    mapper: supportAccountFieldMapper,
  },
  [ActivityTypeEnum.SUPPORT_PRONOUNS]: {
    icon: userIcon,
    label: 'Support pronouns',
    mapper: supportPronounsFieldMapper,
  },
  [ActivityTypeEnum.SUPPORT_BASIC_INFO]: {
    icon: userIcon,
    label: 'Support info',
    mapper: supportBasicInfoFieldMapper,
  },
  [ActivityTypeEnum.SUPPORT_LANGUAGE]: {
    icon: userIcon,
    label: 'Support language(s) spoken',
    mapper: supportLanguagesFieldMapper,
  },
  [ActivityTypeEnum.SUPPORT_CONTACT_PREFERENCES]: {
    icon: userIcon,
    label: 'Support contact preferences',
    mapper: supportContactPreferencesFieldMapper,
  },
  [ActivityTypeEnum.SUPPORT_ETHNICITY]: {
    icon: userIcon,
    label: 'Support ethnicity',
    mapper: supportEthnicityFieldMapper,
  },
  [ActivityTypeEnum.PATIENT_SUPPORT_ADDRESS]: {
    icon: userIcon,
    label: 'Support address',
    mapper: supportAddressFieldMapper,
  },
  [ActivityTypeEnum.PATIENT_TAGS]: {
    icon: userIcon,
    label: 'Tags',
    mapper: tagsFieldMapper,
  },
  [ActivityTypeEnum.SUPPORT_PATIENT_RELATION]: {
    icon: userIcon,
    label: 'Support relationship to patient',
    mapper: supportRelationToPatientFieldMapper,
  },
};
